import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskLines, CTA, Overline } from "./primitives";
import { ENROLL_URL } from "../../config";

export const Hero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-ivory">
      {/* Portrait — right side, parallax (desktop/tablet) */}
      <motion.div
        style={reduce ? undefined : { y: imgY, scale: imgScale }}
        className="absolute right-0 top-0 hidden h-full w-[52%] md:block"
      >
        <div className="glow-float absolute -left-10 top-1/3 -z-0 h-72 w-72 rounded-full bg-terracotta/20 blur-[90px]" />
        <img
          src="/images/hero.jpg"
          alt="Nichole MacDonald, founder of Creating Wealth Through Community"
          data-testid="hero-image"
          className="h-full w-full object-cover object-center"
          style={{
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 18%, #000 46%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 18%, #000 46%)",
          }}
        />
      </motion.div>

      {/* Copy */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-6 pt-28 md:px-10">
        <div className="max-w-2xl">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.1 }}>
            <Overline>Creating Wealth Through Community</Overline>
          </motion.div>

          <h1 className="mt-6 font-display text-[11vw] font-medium leading-[0.95] tracking-tight text-ink sm:text-5xl lg:text-6xl xl:text-[4.4rem]">
            <MaskLines
              lines={["Unlock your", "wealth potential"]}
              delay={0.35}
            />
            <span className="block overflow-hidden">
              <motion.span
                className="block italic text-terracotta"
                initial={reduce ? { opacity: 0 } : { y: "110%" }}
                animate={reduce ? { opacity: 1 } : { y: "0%" }}
                transition={{ duration: 1, delay: 0.35 + 2 * 0.12, ease: [0.22, 1, 0.36, 1] }}
              >
                in one day.
              </motion.span>
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.85 }}
          >
            <p className="mt-6 max-w-xl font-sans text-lg font-light leading-relaxed text-stonegray">
              Discover what's been holding you back, uncover the value you're already sitting on, and see new
              possibilities for creating more money, more confidence, and more freedom.
            </p>
            <p className="mt-5 max-w-xl font-display text-xl italic leading-snug text-ink md:text-2xl">
              One day can create the breakthrough. What comes next can change your life.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-8"
          >
            <CTA href={ENROLL_URL} data-testid="hero-cta">Start for $67</CTA>
            <p className="mt-3 font-sans text-xs font-light tracking-wide text-stonegray">No contract. Cancel anytime.</p>
          </motion.div>

          {/* Mobile-only portrait — fully visible, no fade */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="relative mt-12 md:hidden"
          >
            <div className="glow-float absolute -inset-4 -z-10 rounded-full bg-terracotta/20 blur-3xl" />
            <img
              src="/images/hero.jpg"
              alt="Nichole MacDonald, founder of Creating Wealth Through Community"
              data-testid="hero-image-mobile"
              className="aspect-[4/5] w-full rounded-[2px] object-cover object-top"
            />
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-8 left-6 z-10 hidden items-center gap-3 md:left-10 md:flex"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-terracotta" strokeWidth={1.5} />
        <span className="font-sans text-[0.7rem] uppercase tracking-[0.3em] text-ink/50">Begin the story</span>
      </motion.div>
    </section>
  );
};
